import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { User, Lock, Trash2, Save } from 'lucide-react';

export default function Settings() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile]   = useState({ name: user?.name || '', company: user?.company || '' });
  const [pwForm, setPwForm]     = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [savingProfile, setSP]  = useState(false);
  const [savingPw, setSPw]      = useState(false);
  const [delPass, setDelPass]   = useState('');
  const [deleting, setDeleting] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSP(true);
    try {
      const { data } = await authAPI.updateProfile(profile);
      updateUser(data.user);
      toast.success('Profile updated');
    } catch (err) { toast.error(err.response?.data?.error || 'Update failed'); }
    finally { setSP(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirm) return toast.error('Passwords do not match');
    if (pwForm.newPassword.length < 8) return toast.error('Password must be at least 8 characters');
    setSPw(true);
    try {
      await authAPI.changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      toast.success('Password changed');
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to change password'); }
    finally { setSPw(false); }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    if (!window.confirm('Are you absolutely sure? This will permanently delete your account and all chatbots.')) return;
    setDeleting(true);
    try {
      await authAPI.updateProfile({ deleteAccount: true, password: delPass }); // adjust endpoint as needed
      await logout();
      navigate('/login');
      toast.success('Account deleted');
    } catch (err) { toast.error(err.response?.data?.error || 'Deletion failed'); }
    finally { setDeleting(false); }
  };

  const Section = ({ icon: Icon, title, children }) => (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
        <div style={{ width: 32, height: 32, borderRadius: 'var(--radius)', background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--brand)' }}>
          <Icon size={16} />
        </div>
        <h2 style={{ fontFamily: 'var(--font-head)', fontSize: 16, fontWeight: 700 }}>{title}</h2>
      </div>
      {children}
    </div>
  );

  return (
    <div style={{ maxWidth: 600 }}>
      <div className="page-header"><h1>Settings</h1><p>Manage your account preferences</p></div>

      {/* Profile */}
      <Section icon={User} title="Profile">
        <form onSubmit={saveProfile} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Full name</label>
              <input className="input" value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Company</label>
              <input className="input" value={profile.company} onChange={e => setProfile(p => ({ ...p, company: e.target.value }))} placeholder="Optional" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="input" value={user?.email || ''} disabled style={{ opacity: 0.5, cursor: 'not-allowed' }} />
            <span className="form-hint">Email cannot be changed</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-primary btn-sm" type="submit" disabled={savingProfile}>
              {savingProfile ? 'Saving…' : <><Save size={13} /> Save profile</>}
            </button>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>
              Account created: {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
            </span>
          </div>
        </form>
      </Section>

      {/* Password */}
      <Section icon={Lock} title="Change Password">
        <form onSubmit={savePassword} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            { k: 'currentPassword', l: 'Current password' },
            { k: 'newPassword',     l: 'New password (min 8 chars)' },
            { k: 'confirm',         l: 'Confirm new password' },
          ].map(({ k, l }) => (
            <div className="form-group" key={k}>
              <label className="form-label">{l}</label>
              <input className="input" type="password" placeholder="••••••••" value={pwForm[k]}
                onChange={e => setPwForm(p => ({ ...p, [k]: e.target.value }))} required />
            </div>
          ))}
          <button className="btn btn-primary btn-sm" type="submit" disabled={savingPw} style={{ alignSelf: 'flex-start' }}>
            {savingPw ? 'Changing…' : 'Change password'}
          </button>
        </form>
      </Section>

      {/* Danger zone */}
      <Section icon={Trash2} title="Danger Zone">
        <div style={{ padding: '16px', background: 'rgba(224,82,82,0.06)', borderRadius: 'var(--radius)', border: '1px solid rgba(224,82,82,0.2)' }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--red)' }}>Delete Account</div>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
            Permanently delete your account, all chatbots, leads, and conversation history. This cannot be undone.
          </p>
          <form onSubmit={deleteAccount} style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Enter your password to confirm</label>
              <input className="input" type="password" placeholder="Password" value={delPass}
                onChange={e => setDelPass(e.target.value)} required />
            </div>
            <button className="btn btn-danger" type="submit" disabled={deleting}>
              {deleting ? 'Deleting…' : <><Trash2 size={14} /> Delete</>}
            </button>
          </form>
        </div>
      </Section>
    </div>
  );
}
